import { ThroughputHistory } from '@abrController/DataCollectors/ThroughputHistory/ThroughputHistory';
import { IEstimator } from '@abrController/Estimators/IEstimator';
import { IThroughputEstimatorConfig } from '@abrController/Estimators/ThroughputEstimator/ThroughputEstimator.config';
import { getKBitsSpeedFromPlaylistBitrate } from '@abrController/helpers/normalizeSpeed';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { IResolution } from '@resolutionsManager/IResolutionsManager';
import { NetworkLoaderEvent } from '@streamLoader/net/NetworkLoader.events';
import { INetworkLoadStats } from '@streamLoader/net/helpers/NetworkLoadStats';
import bind from '@utils/decorators/bind';

export class ThroughputEstimator extends ConfigConsumer<IThroughputEstimatorConfig> implements IEstimator {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public additionalInfo: TDebuggableAdditionalInfo = {};
  public groups: string[] = ['abr'];

  private _isResultsConfident: boolean = false;
  private _fragmentsLoadedAmount: number = 0;

  private readonly _throughputHistory: ThroughputHistory;
  private readonly _onNetworkMetricsReadyListener: string;

  constructor(eventBus: TEventBus) {
    super('throughputEstimator', eventBus);
    this._throughputHistory = new ThroughputHistory(this._eventBus);

    this._onNetworkMetricsReadyListener = this._eventBus.subscribe(NetworkLoaderEvent.stats.statsReady, this._onNetworkStatsReady);
  }

  public destroy(): void {
    super.destroy();

    this._throughputHistory.destroy();
    this._eventBus.unsubscribe(this._onNetworkMetricsReadyListener);
  }

  public reset(): void {
    this._throughputHistory.reset();
    this._isResultsConfident = false;
  }

  public getNextEstimatedResolution(availableDecreasingResolutions: IResolution[]): IResolution | null {
    // speeds in kbit/s, ttfb in s
    const { realSpeedWithoutTTFB, measuredSpeed, ttfb } = this._throughputHistory.getCurrentBitrateEstimate();

    this.additionalInfo = {
      realSpeedWithoutTTFB,
      measuredSpeed,
      ttfb,
    };

    if (!availableDecreasingResolutions.length || !measuredSpeed || !this._isResultsConfident) {
      return null;
    }

    const mappedSpeedForFirstResolution: number =
      getKBitsSpeedFromPlaylistBitrate(availableDecreasingResolutions[0].bitrate) /
      (ttfb + getKBitsSpeedFromPlaylistBitrate(availableDecreasingResolutions[0].bitrate) / realSpeedWithoutTTFB);

    if (getKBitsSpeedFromPlaylistBitrate(availableDecreasingResolutions[0].bitrate) < mappedSpeedForFirstResolution) {
      return availableDecreasingResolutions[0];
    }

    const bestMatchingResolution: IResolution | undefined = availableDecreasingResolutions.find(({ bitrate }) => {
      const bwInKbitps: number = getKBitsSpeedFromPlaylistBitrate(bitrate);
      const mappedSpeed: number = bwInKbitps / (ttfb + bwInKbitps / realSpeedWithoutTTFB);

      return (
        mappedSpeed * this.config.optimalResolutionLowerBound <= bwInKbitps &&
        bwInKbitps <= mappedSpeed * this.config.optimalResolutionUpperBound
      );
    });

    if (bestMatchingResolution) {
      return bestMatchingResolution;
    }

    const closestResolution: IResolution | undefined = availableDecreasingResolutions.find(
      ({ bitrate }) => getKBitsSpeedFromPlaylistBitrate(bitrate) <= measuredSpeed * this.config.optimalResolutionLowerBound,
    );

    return closestResolution || availableDecreasingResolutions.slice(-1)[0];
  }

  @bind
  public _handleConfigUpdated(): void {}

  @bind
  private _onNetworkStatsReady(metrics: INetworkLoadStats): void {
    if (metrics.type !== 'binary' || this._isResultsConfident) {
      return;
    }

    this._fragmentsLoadedAmount += 1;

    if (this._fragmentsLoadedAmount >= this.config.amountOfFragmentToBecomeConfident) {
      this._isResultsConfident = true;
    }
  }
}
